import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import { getAll, update } from '../_request';
import { useNavigate,useParams } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import { useToast } from '../../modules/layout/components/ToastContext';
import { IProfileDetailsDto } from '../../interfaces/IUtilities';

const EMPTY_PROFILE: IProfileDetailsDto = {
    UserId: '',
    IsMainUser: false,
    CreatedBy: '',
    Description: '',
    Category: '',
    ProfileId: '',
    PPID: '',
    IdpRealm: '',
    LanguageCode: '',
    Roles: [],
    UnmanagedClaims: [],
    Name: '',
    Username: '',
    UserType: '',
    Email: '',
    ProfileState: '',
    Approved: false,
    MobileNumber: '',  

}

export default function UpdateProfile() {

    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
        
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clearMobileNumber, setClearMobileNumber] = useState(false);

    const [profile, setProfile] = useState<IProfileDetailsDto>(EMPTY_PROFILE);
    const { targetProfileId } = useParams<{ targetProfileId: string }>();


    useEffect(()=>{
        if (!targetProfileId) {
        showToast && showToast(t('NoProfileIdProvided'), 'warning');
        navigate(`/profile-details/:targetProfileId`)  
        return; }

        const fetchProfileDetails = async () =>{
            setLoading(true);
            setError(null);
            try {
                const result: any = await getAll(`/api/UserEvents/getprofiledetails`,`?targetProfileId=${encodeURIComponent(targetProfileId)}`);

                const respStatus = result?.response?.status || result?.status;

                if (respStatus === 200 || respStatus === undefined) {
                    const payload = result?.data?.Data ?? result?.data ?? result;
                    setProfile(payload as IProfileDetailsDto);
                    if (!payload && showToast) {
                        showToast(t('ProfileNotFound'), 'info');
                    }
                } else if (respStatus === 403) {
                    navigate('/error/403');
                } else if (respStatus === 401) {
                    navigate('/auth/login');
                } else if (respStatus === 404) {
                    showToast && showToast(t('ProfileNotFound'), 'error');
                    navigate('/profiles');
                } else if (respStatus === 500) {
                    navigate('/error/500');
                } else {
                    showToast && showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                }
            } catch (err: any) {
                console.error(err);
                setError(String(err?.message ?? err));
                showToast && showToast(t('ConnectionError', { error: String(err) }), 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileDetails();
    }, [targetProfileId]);   


    const handleSubmit = async (values: IProfileDetailsDto) =>{
        setLoading(true);
        setError(null);
        
        
        const payload = {
            name: values.Name,
            email: values.Email,
            description: values.Description,
            languageCode: values.LanguageCode,
            mobileNumber: clearMobileNumber ? '' : values.MobileNumber,
            clearMobileNumber,
        };

        try{
            const result: any = await update(
                `/api/UserEvents/setprofiledetails?targetProfileId=${encodeURIComponent(
                targetProfileId!
                )}`,
                payload
            );
            const respStatus = result?.response?.status || result?.status;

            if (respStatus === 200 || respStatus === 204) {
            showToast && showToast(t('ProfileUpdatedSuccessfully'), 'success');
            } else if (respStatus === 403) {
                navigate('/error/403');
            } else if (respStatus === 401) {
                navigate('/auth/login');
            } else if (respStatus === 404) {
                showToast && showToast(t('ProfileNotFound'), 'error');
                navigate('/profiles');
            } else if (respStatus === 500) {
                navigate('/error/500');
            } else {
                showToast && showToast(t('StatusCode', { code: String(respStatus) }), 'error');
            }
        } catch (err: any) {
            console.error(err);
            setError(String(err?.message ?? err));
            showToast && showToast(t('ConnectionError', { error: String(err) }), 'error');
        } finally {
            setLoading(false);
        }  
    };
    if (!profile && !loading) {
        return <p>{t('ProfileNotFound')}</p>;
    }

    return(
      <div className="container mt-4 gg-container-form">
        <AppBreadcrumb items={[
            { title: t('Home'), href: '/' },
            { title: t('UserProfilesList'), href: '/find-profiles' },
            { title: t('ProfileDetails'), href:  `/profile-details/${encodeURIComponent(targetProfileId!)}`},
            { title: t('EditProfile') }
        ]} />

        <div className="row justify-content-center mt-5">
            <div className="col-12 col-lg-8">
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-light py-3 border-bottom">
                        <div className="d-flex align-item-center">
                            <h5 className="mb-0 fw-bold text-dark">{t('EditProfile')}</h5>
                        </div>
                    </div>

                    <div className="card-body p-4">
                        {loading && <Loader />}
                        {error && <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            {error}
                        </div>}

                        <Formik<IProfileDetailsDto> initialValues={profile} enableReinitialize onSubmit={handleSubmit}>
                            {({ dirty, isValid, values, setFieldValue }) => (
                                <Form>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-secondary">{t('Email')}</label>
                                            <Field name="Email" type="email" className="form-control form-control-lg fs-6" />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-secondary">{t('MobileNumber')}</label>
                                            <div className="input-group">
                                                <Field
                                                    name="MobileNumber"
                                                    className="form-control form-control-lg fs-6"
                                                    value={clearMobileNumber ? '' : values.MobileNumber}
                                                    placeholder={clearMobileNumber ? t('WillBeCleared') : undefined}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setClearMobileNumber(false);
                                                        setFieldValue('MobileNumber', e.target.value);
                                                    }}
                                                />
                                                {(values.MobileNumber || clearMobileNumber) && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        title={t('ClearMobileNumber')}
                                                        onClick={() => {
                                                            setClearMobileNumber(true);
                                                            setFieldValue('MobileNumber', '');
                                                        }}
                                                    >
                                                        <i className="bi bi-x-lg" />
                                                    </button>
                                                )}
                                            </div>
                                            {clearMobileNumber && (
                                                <small className="text-warning">
                                                    {t('MobileNumberWillBeCleared')}
                                                </small>
                                            )}
                                        </div>

                                        <div className="col-12 mt-5">
                                            <div className="d-flex flex-column flex-sm-row justify-content-end gap-3 pt-3 border-top">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary text-white"
                                                    onClick={() => navigate(`/profile-details/${encodeURIComponent(targetProfileId!)}`)}
                                                >
                                                    {t('Cancel')}
                                                </button>

                                                <button
                                                    type="submit"
                                                    className="gg-btn gg-btn-submit px-5 py-2 order-1 order-sm-2"
                                                    disabled={!dirty || !isValid || loading}
                                                >
                                                    {t('SaveChanges')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    </div>  
    );
};
